import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * FinancePinContext — single source of truth for the PIN economic gate.
 *
 * iPhone-like behaviour (per spec):
 *   • Set the PIN once in Settings, stored permanently.
 *   • Asked only to *show* economic data via the discreet shield icon.
 *   • Stays unlocked across SPA navigation within the session.
 *   • Refresh / browser close / logout → automatically masked again.
 *
 * The unlock state is a plain in-memory boolean — no timeout, no
 * expiry, no countdown. Page reload obliterates it, that's the
 * mechanism by spec.
 *
 * IMPORTANT — auth gating:
 *   The status probe (`GET /finance-pin/status`) is gated behind a
 *   logged-in `user`. Calling it during the anonymous boot flash would
 *   trigger the global 401 → /auth/refresh → forced /login redirect
 *   pipeline, breaking every subsequent login attempt.
 */

const Ctx = createContext(null);

export function FinancePinProvider({ children }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [planEligible, setPlanEligible] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [openModalImpl, setOpenModalImplState] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await api.get("/settings/finance-pin/status");
      const d = r?.data || {};
      setEnabled(!!d.enabled);
      setPlanEligible(!!d.plan_eligible);
    } catch {
      // On any error fall back to safe defaults — masking stays "off"
      // so we don't accidentally hide things from a free-plan user
      // whose status probe momentarily glitched.
      setEnabled(false);
      setPlanEligible(false);
    }
  }, []);

  // Probe the backend only when authenticated. Re-probe on auth flips so
  // the shield reflects the configuration of the just-logged-in user.
  useEffect(() => {
    if (user && typeof user === "object") {
      refreshStatus();
    } else if (user === false) {
      // Anonymous → reset everything quietly without hitting the API.
      setEnabled(false);
      setPlanEligible(false);
      setUnlocked(false);
    }
    // user === null → still checking, do nothing.
  }, [user, refreshStatus]);

  // Re-probe on tab focus so a settings change made in another tab is
  // picked up promptly — but only when authenticated.
  useEffect(() => {
    if (!(user && typeof user === "object")) return undefined;
    const onFocus = () => refreshStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, refreshStatus]);

  const unlock = useCallback(async (pin) => {
    try {
      await api.post("/settings/verify-finance-pin", { pin });
      setUnlocked(true);
      return { ok: true };
    } catch (err) {
      // Treat every non-success as "PIN non corretto" — the spec wants a
      // single, simple error message. The backend returns 401 for wrong
      // PIN, 400 if the gate is somehow disabled, 422 if the input
      // doesn't match the 4-6 digit regex (e.g. user typed letters).
      // From the operator's standpoint they all mean "try again".
      return { ok: false, error: "PIN non corretto." };
    }
  }, []);

  const relock = useCallback(() => setUnlocked(false), []);

  const setOpenPinModalImpl = useCallback((fn) => {
    setOpenModalImplState(() => fn);
  }, []);
  const openPinModal = useCallback(() => {
    if (typeof openModalImpl === "function") openModalImpl();
  }, [openModalImpl]);

  const value = useMemo(() => ({
    enabled,
    planEligible,
    unlocked,
    unlock,
    relock,
    refreshStatus,
    openPinModal,
    setOpenPinModalImpl,
  }), [enabled, planEligible, unlocked, unlock, relock, refreshStatus, openPinModal, setOpenPinModalImpl]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFinancePin() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Defensive fallback so a missing provider can't blank the page.
    return {
      enabled: false,
      planEligible: false,
      unlocked: true,
      unlock: async () => ({ ok: false, error: "Provider non montato" }),
      relock: () => {},
      refreshStatus: async () => {},
      openPinModal: () => {},
      setOpenPinModalImpl: () => {},
    };
  }
  return ctx;
}
